import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { AuthService, StateWithProcess } from '@spartacus/core';
import { NEVER, Observable, merge, timer } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap, switchMapTo, tap, withLatestFrom } from 'rxjs/operators';
import { UserIdService } from '../../auth';
import { IdleService } from '../../idle';
import { PrincipalConfiguration } from '../../model';
import { FEED_POLLING_PERIOD, Feed } from '../../model/feed.model';
import { FeedFacade, PrincipalConfigurationService } from '../index';
import { UserActions } from '../store/actions';
import { FeedSelectors } from '../store/selectors/user.selectors';
import { StateWithUser } from '../store/user-state';

@Injectable()
export class FeedService implements FeedFacade {
  constructor(
    private authService: AuthService,
    private store: Store<StateWithUser & StateWithProcess<any>>,
    private userIdService: UserIdService,
    private principalConfigurationService: PrincipalConfigurationService,
    private idleService: IdleService
  ) {}

  // pollFeedData$ will never emit any values (we only care about its side effects), so we
  // can cast it to Observable<never>, allowing it to be merged with any other observable.
  private pollFeedData$: Observable<never> = this.authService.isUserLoggedIn().pipe(
    distinctUntilChanged(),
    switchMap((isUserLoggedIn) =>
      isUserLoggedIn
        ? this.idleService.getIdle().pipe(switchMap((idle) => (!idle ? timer(0, FEED_POLLING_PERIOD) : NEVER)))
        : NEVER
    ),
    tap(() => this.loadFeedData()),
    switchMapTo(NEVER), // don't emit any values
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * Trigger loading of feed data.
   *
   * NOTE: This is usually not needed as getFeedData() triggers this automatically at regular
   * intervals.  Only use loadFeedData() if feed data is known to have been manipulated (e.g. by the caller).
   */
  loadFeedData(): void {
    this.userIdService
      .takeUserId(true)
      .pipe(withLatestFrom(this.store.pipe(select(FeedSelectors.getFeedData))))
      .subscribe(
        ([userId, feed]) => this.store.dispatch(new UserActions.LoadFeedData(userId, feed?.feedRequestList)),
        () => {}
      );
  }

  getFeedData(): Observable<Feed> {
    return merge(this.store, this.pollFeedData$).pipe(
      select(FeedSelectors.getFeedState),
      withLatestFrom(this.authService.isUserLoggedIn()),
      map(([loaderState, isUserLoggedIn]) => {
        if (isUserLoggedIn && loaderState.value) {
          return loaderState.value;
        } else {
          return undefined;
        }
      }),
      filter((feedData) => !!feedData?.configurations),
      tap((feedData: Feed) => this.updateSapOnlineFunctionsConfig(feedData.configurations)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private updateSapOnlineFunctionsConfig(configurations: PrincipalConfiguration[]): void {
    const sapOnlineFunctionsConfig = configurations.find((config) => config.code === 'enableSapOnlineFunctions');
    this.principalConfigurationService.manuallyUpdateValue(sapOnlineFunctionsConfig);
  }
}
